import { addDays, lastCompleteDay } from '../ProductPerformance/model';

export type NumericRow = Record<string, string | number | null | undefined>;
export type Row = NumericRow;
export const dimensionLabel = (value: unknown) =>
  value === null || value === undefined ? 'Unknown' : value === '' ? '(blank)' : String(value);
export type Filters = {
  from: string;
  to: string;
  channel: string;
  market: string;
  campaign: string;
  mode: 'Unique' | 'Total';
};
export function sum(rows: Array<NumericRow>, key: string): number | null {
  const values = rows.map((r) => r[key]).filter((n): n is number => typeof n === 'number');
  return values.length ? values.reduce((a, b) => a + b, 0) : null;
}
export const ratio = (n: number | null, d: number | null) => (n === null || d === null || d === 0 ? null : n / d);
export type Metrics = ReturnType<typeof metrics>;
export function metrics(rows: Array<NumericRow>, mode: Filters['mode']) {
  const p = mode === 'Unique' ? 'unique_' : '';
  const sends = sum(rows, p + 'send_cnt'),
    opens = sum(rows, p + 'open_cnt'),
    clicks = sum(rows, p + 'click_cnt'),
    unsubs = sum(rows, p + 'unsubscribe_cnt'),
    sessions = sum(rows, mode === 'Unique' ? 'unique_sessions' : 'sessions'),
    orders = sum(rows, 'orders'),
    ttv = sum(rows, 'ttv'),
    gm = sum(rows, 'gm');
  return {
    sends,
    opens,
    clicks,
    unsubs,
    sessions,
    orders,
    ttv,
    gm,
    openRate: ratio(opens, sends),
    clickRate: ratio(clicks, sends),
    cto: ratio(clicks, opens),
    unsubRate: ratio(unsubs, sends),
    purchaseRate: ratio(orders, clicks),
    sessionRatio: ratio(sessions, clicks),
    sessionsPerSend: ratio(sessions, sends),
    gmPerSend: ratio(gm, sends),
    aov: ratio(ttv, orders),
    ttvPerClick: ratio(ttv, clicks),
    valuePerSend: ratio(ttv, sends),
  };
}
export const REPORT_VIEWS = [
  'Tables: Key Metrics',
  'Charts: Comms Metrics',
  'Charts: Financial Metrics',
  'Charts: Health Metrics',
  'Clicks Insights',
  'Clicks Insights (Strategic Regions)',
  'Offer Clicks Insights',
  'Offer Vendor Insights',
] as const;
export type ReportView = (typeof REPORT_VIEWS)[number];
export const CADENCES = ['Daily', 'Weekly', 'Monthly', 'Annually', 'Weekday', 'None'] as const;
export type Cadence = (typeof CADENCES)[number];
export const splitColumns = {
  All: 'All',
  'Country Group': 'subscriber_country_group',
  'UTM Medium': 'utm_medium',
  'UTM Campaign': 'utm_campaign',
  Country: 'subscriber_country',
  Region: 'subscriber_region',
  'UTM Source': 'utm_source',
  'Campaign + Journey': 'campaign_journey',
  'Segment Name': 'segment_name',
  'Campaign Name': 'campaign_name',
  'Journey Name': 'journey_name',
  'Message Name': 'message_name',
  'Experiment Name': 'experiment_name',
  'TE Cadence': 'te_cadence',
  'LuxPlus Status': 'luxplus_status',
  Platform: 'platform',
  'Email Name': 'email_name',
  'Brand Name': 'brand_name',
  Offers: 'offers',
  'Hero Offers': 'hero_offers',
  'Dispatch ID': 'dispatch_id',
} as const;
export const SPLITS = Object.keys(splitColumns) as Array<keyof typeof splitColumns>;
export type Split = keyof typeof splitColumns;
export type ReportState = {
  filters: Filters;
  view: ReportView;
  cadence: Cadence;
  split: Split;
  timeSplit: 'Date Cadence' | 'Full Period';
  product: string;
  clickFrom: string;
  clickTo: string;
  /**
   * Send dates for Strategic Regions, Offer Clicks and Offer Vendor, one pair per tab so a wide window on one tab
   * never carries into another. Empty means the tab's Tableau default.
   */
  regionsFrom: string;
  regionsTo: string;
  offersFrom: string;
  offersTo: string;
  vendorsFrom: string;
  vendorsTo: string;
};
export type Measure = keyof Metrics;
export const MEASURES: Array<{ key: Measure; label: string; digits: number; kind: 'count' | 'percent' | 'money' }> = [
  { key: 'sends', label: 'Sends', digits: 0, kind: 'count' },
  { key: 'opens', label: 'Opens', digits: 0, kind: 'count' },
  { key: 'openRate', label: 'Open Rate', digits: 3, kind: 'percent' },
  { key: 'clicks', label: 'Clicks', digits: 0, kind: 'count' },
  { key: 'clickRate', label: 'Click Rate', digits: 3, kind: 'percent' },
  { key: 'cto', label: 'Click to Open Rate', digits: 3, kind: 'percent' },
  { key: 'unsubs', label: 'Unsubscribes', digits: 0, kind: 'count' },
  { key: 'unsubRate', label: 'Unsub Rate', digits: 3, kind: 'percent' },
  { key: 'sessions', label: 'Sessions', digits: 0, kind: 'count' },
  { key: 'sessionRatio', label: 'Sessions / Clicks', digits: 0, kind: 'percent' },
  { key: 'sessionsPerSend', label: 'Sessions / Sends', digits: 2, kind: 'percent' },
  { key: 'orders', label: 'Orders', digits: 0, kind: 'count' },
  { key: 'purchaseRate', label: 'Purchase Rate', digits: 3, kind: 'percent' },
  { key: 'gm', label: 'GM (A$)', digits: 0, kind: 'money' },
  { key: 'gmPerSend', label: 'GM / Send (A$)', digits: 3, kind: 'money' },
  { key: 'ttv', label: 'TTV (A$)', digits: 0, kind: 'money' },
  { key: 'aov', label: 'AOV (A$)', digits: 0, kind: 'money' },
  { key: 'valuePerSend', label: 'TTV / Send (A$)', digits: 3, kind: 'money' },
  { key: 'ttvPerClick', label: 'TTV / Click (A$)', digits: 3, kind: 'money' },
];
export function selection(value: string): Array<string> {
  if (value === 'All') {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
      return parsed;
    }
  } catch {
    /* Single original URL member. */
  }
  return [value];
}
export function selectionValue(values: Array<string>): string {
  return values.length === 0 ? 'All' : values.length === 1 ? values[0] : JSON.stringify(values);
}
const legacy: Record<string, ReportView> = {
  'Key metrics': REPORT_VIEWS[0],
  Engagement: REPORT_VIEWS[1],
  Commercial: REPORT_VIEWS[2],
  'Click insights': REPORT_VIEWS[4],
};
/** Explicit old-native URL projection into the preserved report controls. */
export function normalizeReportParams(input: URLSearchParams): { params: URLSearchParams; problem: string | null } {
  const params = new URLSearchParams(input);
  let problem: string | null = null;
  const map = (old: string, key: string, convert: (raw: string) => string | undefined) => {
    if (!input.has(old)) {
      return;
    }
    const value = convert(input.get(old));
    if (value === undefined || (input.has(key) && input.get(key) !== value)) {
      problem ??= `Invalid or conflicting ${old} URL projection.`;
      return;
    }
    params.set(key, value);
    params.delete(old);
  };
  const cadence: Record<string, Cadence> = {
    day: 'Daily',
    week: 'Weekly',
    month: 'Monthly',
    year: 'Annually',
    weekday: 'Weekday',
    none: 'None',
    full: 'None',
  };
  map('grain', 'cadence', (raw) => cadence[raw]);
  map('data_split', 'split', (raw) => SPLITS.find((key) => splitColumns[key] === raw));
  map('click_from', 'clickFrom', (raw) => raw);
  map('click_to', 'clickTo', (raw) => raw);
  map('time_split', 'timeSplit', (raw) =>
    raw === 'full' ? 'Full Period' : raw === 'cadence' ? 'Date Cadence' : undefined,
  );
  if (input.get('grain') === 'full') {
    if (input.has('timeSplit') && input.get('timeSplit') !== 'Full Period') {
      problem ??= 'Full native grain conflicts with the selected time split.';
    }
    params.set('timeSplit', 'Full Period');
  }
  if (params.has('cadence') && !CADENCES.includes(params.get('cadence') as Cadence)) {
    problem ??= 'Choose a supported cadence.';
  }
  if (params.has('split') && !SPLITS.includes(params.get('split') as Split)) {
    problem ??= 'Choose a supported data split.';
  }
  if (params.has('timeSplit') && !['Date Cadence', 'Full Period'].includes(params.get('timeSplit'))) {
    problem ??= 'Choose a supported time split.';
  }
  return { params, problem };
}
/**
 * Tableau's saved date filters, both relative to today in Sydney:
 * Send Date "Last 4 weeks" starts on the Sunday three weeks before the current (Sunday-start) week;
 * Clicks by Date "Last 14 days" starts 13 days before today.
 * Both end on the last complete day here, which is the newest data either source holds.
 */
export function defaultSendFrom(now = new Date()): string {
  const today = addDays(lastCompleteDay(now), 1);
  return addDays(today, -new Date(`${today}T00:00:00Z`).getUTCDay() - 21);
}
export const defaultClickFrom = (now = new Date()) => addDays(lastCompleteDay(now), -12);
export function readReport(input: URLSearchParams, now = new Date()): ReportState {
  const params = normalizeReportParams(input).params;
  const end = lastCompleteDay(now);
  const filters: Filters = {
    from: params.get('from') ?? defaultSendFrom(now),
    to: params.get('to') ?? end,
    channel: params.get('channel') ?? 'All',
    market: params.get('market') ?? 'All',
    campaign: params.get('campaign') ?? 'All',
    mode: (params.get('mode') ?? params.get('countMode'))?.toLowerCase() === 'total' ? 'Total' : 'Unique',
  };
  filters.market = selectionValue(
    selection(filters.market).map((value) =>
      value === 'Unknown' || value === '(blank)' ? value : value.toLowerCase(),
    ),
  );
  const nativeViews: Record<string, ReportView> = {
    tables: REPORT_VIEWS[0],
    comms: REPORT_VIEWS[1],
    financial: REPORT_VIEWS[2],
    health: REPORT_VIEWS[3],
    clicks: REPORT_VIEWS[4],
    regions: REPORT_VIEWS[5],
    offers: REPORT_VIEWS[6],
    vendors: REPORT_VIEWS[7],
  };
  const candidate = params.get('commsView') ?? params.get('view') ?? nativeViews[params.get('reportView') ?? ''] ?? '';
  return {
    filters,
    view: REPORT_VIEWS.find((v) => v === candidate) ?? legacy[candidate] ?? REPORT_VIEWS[0],
    cadence: CADENCES.find((v) => v === params.get('cadence')) ?? 'Daily',
    split: SPLITS.find((v) => v === params.get('split')) ?? 'All',
    timeSplit: params.get('timeSplit') === 'Full Period' ? 'Full Period' : 'Date Cadence',
    product: params.get('product') ?? 'All',
    clickFrom: params.get('clickFrom') ?? params.get('trend_from') ?? defaultClickFrom(now),
    clickTo: params.get('clickTo') ?? params.get('trend_to') ?? end,
    regionsFrom: params.get('regionsFrom') ?? '',
    regionsTo: params.get('regionsTo') ?? '',
    offersFrom: params.get('offersFrom') ?? '',
    offersTo: params.get('offersTo') ?? '',
    vendorsFrom: params.get('vendorsFrom') ?? '',
    vendorsTo: params.get('vendorsTo') ?? '',
  };
}
export function reportParams(state: ReportState): URLSearchParams {
  return new URLSearchParams({
    ...state.filters,
    commsView: state.view,
    cadence: state.cadence,
    split: state.split,
    timeSplit: state.timeSplit,
    product: state.product,
    clickFrom: state.clickFrom,
    clickTo: state.clickTo,
    regionsFrom: state.regionsFrom,
    regionsTo: state.regionsTo,
    offersFrom: state.offersFrom,
    offersTo: state.offersTo,
    vendorsFrom: state.vendorsFrom,
    vendorsTo: state.vendorsTo,
  });
}
/** Write explicit UI edits even while an unrelated URL field is invalid. */
export function editReportParams(
  params: URLSearchParams,
  state: ReportState,
  changes: Partial<ReportState>,
): { params: URLSearchParams; state: ReportState } {
  const nextState = { ...state, ...changes },
    next = new URLSearchParams(params),
    serialized = reportParams(nextState);
  const aliases: Record<string, string> = {
    cadence: 'grain',
    split: 'data_split',
    timeSplit: 'time_split',
    clickFrom: 'click_from',
    clickTo: 'click_to',
  };
  const keys = Object.keys(changes).flatMap((key) => (key === 'filters' ? Object.keys(changes.filters) : [key]));
  for (const key of keys) {
    const parameter = key === 'view' ? 'commsView' : key;
    if (serialized.has(parameter)) {
      next.set(parameter, serialized.get(parameter));
    }
    if (key === 'view') {
      next.delete('view');
    }
    if (aliases[key]) {
      next.delete(aliases[key]);
    }
  }
  return { params: next, state: nextState };
}

/** Persist edited controls while leaving untouched rolling date defaults out of the address. */
export function syncReportParams(params: URLSearchParams, state: ReportState, now = new Date()): URLSearchParams {
  const next = normalizeReportParams(params).params;
  const incoming = reportParams(readReport(params, now));
  const displayed = reportParams(state);
  displayed.forEach((value, key) => {
    if (value !== incoming.get(key)) {
      next.set(key, value);
    }
  });
  if (params.has('view')) {
    next.set('commsView', state.view);
    next.delete('view');
  }
  return next;
}
export type TableRecord = { id: string; split: string; time: string; values: Metrics };
export type TimeRecord = { name: string; values: Record<string, Metrics> };
export type PlotRow = { name: string } & Record<string, string | number | null>;
export type Report = {
  state: ReportState;
  ready: boolean;
  identity: string;
  /** Newest send date in the tab's source table, from the source_as_of card. */
  sourceAsOf: string | null;
  scope: Record<string, unknown>;
  sourceRows: Array<NumericRow>;
  totals: Metrics;
  tableRows: Array<TableRecord>;
  series: Array<string>;
  timeline: Array<TimeRecord>;
  clickRows: Array<NumericRow>;
  productRows: Array<NumericRow>;
  clickDimension: string;
  products: Array<string>;
  clickTimeline: Array<PlotRow>;
  clickTotal: number | null;
};
export function chartRows(report: Report, measure: Measure): Array<PlotRow> {
  return report.timeline.map((row) => ({
    name: row.name,
    ...Object.fromEntries(report.series.map((name, i) => ['s' + i, row.values[name]?.[measure] ?? null])),
  }));
}
export function validation(f: Filters) {
  const valid = (date: string) =>
    date.slice(0, 4) !== '0000' &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    !Number.isNaN(Date.parse(date)) &&
    new Date(date).toISOString().slice(0, 10) === date;
  return valid(f.from) && valid(f.to) && f.from <= f.to ? null : 'Enter a valid date range.';
}
/** Measures that add up across rows. With no matching rows these are 0; ratios have no value and show a dash. */
export const ADDITIVE_MEASURES: ReadonlySet<Measure> = new Set<Measure>([
  'sends',
  'opens',
  'clicks',
  'unsubs',
  'sessions',
  'orders',
  'gm',
  'ttv',
]);
/** Shown for a ratio with no denominator, or a value the source does not have. */
export const NO_VALUE = '–';
export function metricFormat(value: number | null | undefined, measure: Measure, noMatchingRows = false): string {
  if (value == null) {
    if (!noMatchingRows || !ADDITIVE_MEASURES.has(measure)) {
      return NO_VALUE;
    }
    value = 0;
  }
  const def = MEASURES.find((m) => m.key === measure);
  return (
    new Intl.NumberFormat('en-AU', { minimumFractionDigits: def.digits, maximumFractionDigits: def.digits }).format(
      def.kind === 'percent' ? value * 100 : value,
    ) + (def.kind === 'percent' ? '%' : '')
  );
}
export function axisFormat(value: number, measure: Measure): string {
  const def = MEASURES.find((m) => m.key === measure);
  if (def.kind === 'percent') {
    return (value * 100).toFixed(measure === 'unsubRate' ? 3 : measure === 'purchaseRate' ? 2 : 1) + '%';
  }
  const amount =
    Math.abs(value) >= 1000000
      ? (value / 1000000).toFixed(1) + 'M'
      : Math.abs(value) >= 1000
        ? (value / 1000).toFixed(0) + 'k'
        : String(Number(value.toFixed(def.digits)));
  return (def.kind === 'money' ? 'A$' : '') + amount;
}
export const SERIES_COLOURS = ['#0F464F', '#4EFEB9', '#5E8C92', '#A9C3C6', '#B8B0A0', '#0F0C08'];
export function seriesStyle(index: number) {
  return {
    colour: SERIES_COLOURS[index % SERIES_COLOURS.length],
    dash: index < SERIES_COLOURS.length ? undefined : index < 12 ? '6 3' : '2 3',
    pattern: index < 6 ? 0 : 1 + (Math.floor(index / 6) % 2),
  };
}
